<template>
  <div class="resposta-textarea">
    <div v-if="textoDestaque" class="observacao-destaque">
      <p>{{ textoDestaque }}</p>
    </div>

    <b-form-textarea
      v-if="!ultimaPergunta"
      ref="textarea"
      rows="8"
      max-rows="8"
      :value="valorTexto"
      :state="inputState"
      :disabled="disabled"
      :maxlength="quantidadeMaximaCaracteres"
      :class="{ 'textarea-custom': true }"
      placeholder="Digite sua resposta"
      @input="atualizarValor"
      @blur="aoPerderFoco"
    />

    <div class="contador-caracteres d-flex justify-content-center mt-1">
      <span
        class="badge position-absolute font-size-14 d-flex align-items-center justify-content-center"
        :class="corContador"
      >
        {{ valorTexto.length }} / {{ quantidadeMaximaCaracteres }}
      </span>
    </div>

    <div
      v-if="valorTexto.length === quantidadeMaximaCaracteres"
      class="text-warning small text-center"
    >
      <span class="font-size-14">
        Limite máximo de {{ quantidadeMaximaCaracteres }} caracteres atingido
      </span>
    </div>

    <div v-if="exibirAvisoMinimo" class="text-danger small text-center">
      <span class="font-size-14">
        {{ textoAvisoMinimo }}
      </span>
    </div>
  </div>
</template>

<script lang="js">
export default {
  name: "RespostaTextarea",

  props: {
    pergunta: {
      type: Object,
      required: true
    },
    modelValue: {
      type: [String, Number, Array],
      default: ""
    },
    disabled: {
      type: Boolean,
      default: false
    },
    quantidadeMaximaCaracteres: {
      type: Number,
      default: 4000
    },
    quantidadeMinimaCaracteres: {
      type: Number,
      default: 15
    }
  },

  data() {
    return {
      foiDigitado: false,
      valorInicialObservacao: "",
      usuarioAlterouValor: false
    };
  },

  mounted() {
    this.sincronizarValorInicial();
    this.verificarFocus();
    this.verificarConteudoExistente();
  },

  watch: {
    valorTexto: {
      handler(newValor) {
        const respostaValida =
          newValor.length >= this.quantidadeMinimaCaracteres &&
          newValor.length <= this.quantidadeMaximaCaracteres;

        this.$emit("resposta-valida", {
          valida: respostaValida,
          comprimento: newValor.length,
          minimo: this.quantidadeMinimaCaracteres,
          maximo: this.quantidadeMaximaCaracteres
        });
      },
      immediate: true
    },

    modelValue: {
      handler() {
        this.verificarConteudoExistente();
      },
      immediate: true
    },

    pergunta: {
      handler() {
        this.sincronizarValorInicial();
        this.usuarioAlterouValor = false;
        this.verificarConteudoExistente();
      },
      immediate: true,
      deep: true
    }
  },

  computed: {
    isObrigatorioEspecial() {
      return this.pergunta?.obrigatorio === "E";
    },

    valorTexto: {
      get() {
        const model = this.modelValue;

        if (model !== null && model !== undefined && String(model).length > 0) {
          return String(model);
        }

        return this.usuarioAlterouValor ? String(model || "") : this.valorInicialObservacao;
      },

      set(valor) {
        this.$emit("update:modelValue", valor);
      }
    },

    textoDestaque() {
      if (!Array.isArray(this.pergunta?.respostas)) {
        return "";
      }

      return this.pergunta.respostas[0]?.ds_resposta || "";
    },

    ultimaPergunta() {
      return this.pergunta?.ultima_pergunta;
    },

    inputState() {
      if (this.isObrigatorioEspecial) {
        return null;
      }

      if (!this.foiDigitado) return null;

      if (this.valorTexto.length < this.quantidadeMinimaCaracteres) {
        return false;
      }

      return this.valorTexto.length <= this.quantidadeMaximaCaracteres;
    },

    corContador() {
      const cores = {
        valido: "bg-success",
        abaixoMinimo: "bg-danger",
        limiteMaximo: "bg-warning",
        naoDigitado: "bg-secondary"
      };

      if (this.isObrigatorioEspecial) {
        return this.foiDigitado ? cores.valido : cores.naoDigitado;
      }

      if (!this.foiDigitado || this.valorTexto.length === 0) {
        return cores.naoDigitado;
      }

      if (this.valorTexto.length === this.quantidadeMaximaCaracteres) {
        return cores.limiteMaximo;
      }

      if (this.valorTexto.length < this.quantidadeMinimaCaracteres) {
        return cores.abaixoMinimo;
      }

      return cores.valido;
    },

    exibirAvisoMinimo() {
      if (this.isObrigatorioEspecial) {
        return false;
      }

      if (!this.foiDigitado) {
        return false;
      }

      return (
        this.valorTexto.length > 0 &&
        this.valorTexto.length < this.quantidadeMinimaCaracteres
      );
    },

    textoAvisoMinimo() {
      const faltam = this.quantidadeMinimaCaracteres - this.valorTexto.length;
      return `Mínimo de ${this.quantidadeMinimaCaracteres} caracteres (faltam ${faltam})`;
    }
  },

  methods: {
    verificarConteudoExistente() {
      this.foiDigitado = Boolean(this.valorTexto && this.valorTexto.length > 0);
    },

    atualizarValor(valor) {
      this.foiDigitado = valor.length > 0;
      this.usuarioAlterouValor = true;

      if (valor.length <= this.quantidadeMaximaCaracteres) {
        this.$emit("update:modelValue", valor);
      }
    },

    aoPerderFoco() {
      this.verificarConteudoExistente();
    },

    verificarFocus() {
      if (this.valorTexto && this.valorTexto.trim()) {
        this.$nextTick(() => {
          if (this.$refs.textarea) {
            this.$refs.textarea.focus();
          }
        });
      }
    },

    verificaDsObservacao() {
      const respostas = this.pergunta?.respostas || [];
      return respostas.find(r => r.ds_observacao)?.ds_observacao || "";
    },

    sincronizarValorInicial() {
      this.valorInicialObservacao = this.verificaDsObservacao();
    }
  }
};
</script>

<style lang="scss" scoped>
.resposta-textarea {
  padding: 10px;
  display: flex;
  height: 285px;
  flex-direction: column;
}

.textarea-custom {
  width: 100%;
  resize: none;
  border-radius: 12px;
  background: #f8fafc;
  padding: 10px !important;
  transition: all 0.2s ease;
  font-size: 16px !important;
  border: 2px solid #e2e8f0;
}

.textarea-custom textarea {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #d8e1ea;
  border-radius: 12px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  box-sizing: border-box;
  background: #ffffff;
  resize: vertical;
}

.textarea-custom:focus {
  outline: none;
  background: #f8fafc;
  border-color: #0d9b49;
  box-shadow: 0 0 0 3px rgba(13, 155, 73, 0.12);
}

.textarea-custom {
  &.is-valid {
    border-color: #28a745 !important;

    &:focus {
      border-color: #28a745 !important;
      box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25) !important;
    }
  }

  &.is-invalid {
    border-color: #dc3545 !important;

    &:focus {
      border-color: #dc3545 !important;
      box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
    }
  }
}

.contador-caracteres {
  height: 21px;
}

.badge {
  min-width: 90px;
}

.observacao-destaque p {
  margin-bottom: 8px;
}
</style>
